import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { daysUntilBirthday } from '../lib/dateUtils'

// People CRM (migration_123). Sorting puts whoever needs attention
// first: overdue contact (past their cadence), then upcoming
// birthdays, then alphabetical.

export function usePeople() {
  const { user } = useAuth()
  const userId = user?.id || null
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refetch = useCallback(async () => {
    if (!userId) return
    const { data, error: err } = await supabase
      .from('people')
      .select('*')
      .eq('user_id', userId)
      .eq('archived', false)
      .order('name')
    if (err) { setError(err.message); return }
    setError(null)
    setRows(data || [])
  }, [userId])

  useEffect(() => {
    if (!userId) { setLoading(false); return }
    refetch().then(() => setLoading(false))
  }, [userId, refetch])

  const people = useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA')
    const enriched = rows.map((p) => {
      const overdueDays = p.contact_every_days && p.last_contact
        ? Math.floor((Date.parse(today) - Date.parse(p.last_contact)) / 86400000) - p.contact_every_days
        : (p.contact_every_days && !p.last_contact ? 1 : null)
      return {
        ...p,
        overdueDays: overdueDays != null && overdueDays > 0 ? overdueDays : null,
        birthdayIn: p.birthday ? daysUntilBirthday(p.birthday) : null,
      }
    })
    return enriched.sort((a, b) => {
      const ao = a.overdueDays != null ? 0 : 1
      const bo = b.overdueDays != null ? 0 : 1
      if (ao !== bo) return ao - bo
      const ab = a.birthdayIn != null && a.birthdayIn <= 14 ? a.birthdayIn : 999
      const bb = b.birthdayIn != null && b.birthdayIn <= 14 ? b.birthdayIn : 999
      if (ab !== bb) return ab - bb
      return (a.name || '').localeCompare(b.name || '')
    })
  }, [rows])

  const save = useCallback(async (values, id = null) => {
    const payload = {
      user_id: userId,
      name: (values.name || '').trim(),
      relation: (values.relation || '').trim() || null,
      birthday: values.birthday || null,
      contact_every_days: values.contact_every_days ? Number(values.contact_every_days) : null,
      notes: (values.notes || '').trim() || null,
      updated_at: new Date().toISOString(),
    }
    const query = id
      ? supabase.from('people').update(payload).eq('id', id)
      : supabase.from('people').insert(payload)
    const { error: err } = await query
    if (err) { setError(err.message); return false }
    await refetch()
    return true
  }, [userId, refetch])

  const remove = useCallback(async (id) => {
    const { error: err } = await supabase.from('people').delete().eq('id', id)
    if (err) { setError(err.message); return false }
    await refetch()
    return true
  }, [refetch])

  // "Contacted now" — log row + bump last_contact in one go.
  const logContact = useCallback(async (personId, note = null) => {
    const today = new Date().toLocaleDateString('en-CA')
    const [logRes, updRes] = await Promise.all([
      supabase.from('people_logs').insert({ user_id: userId, person_id: personId, date: today, note }),
      supabase.from('people').update({ last_contact: today, updated_at: new Date().toISOString() }).eq('id', personId),
    ])
    if (logRes.error || updRes.error) { setError((logRes.error || updRes.error).message); return false }
    await refetch()
    return true
  }, [userId, refetch])

  return { people, loading, error, save, remove, logContact, refetch }
}
